/**
 * `<gallery-lightbox>` — wraps a gallery and opens clicked images in a fullscreen dialog.
 *
 * Any child element with `data-lightbox-src` is treated as a gallery item.
 * Clicking it opens a native `<dialog>` with the full-size image.
 *
 * @attr {string} data-lightbox-src  Full-size image URL (on each gallery item)
 * @attr {string} data-lightbox-alt  Alt text for the full-size image (on each gallery item)
 */
class GalleryLightbox extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #dialogImg = null;
  /** @type {HTMLElement[]} */
  #items = [];
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number} */
  #loadGeneration = 0;

  connectedCallback() {
    this.#items = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('[data-lightbox-src]')]
    );
    if (!this.#items.length) return;
    this.#buildDialog();
    this.addEventListener('click', this.#handleItemClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleItemClick);
    this.#dialog?.remove();
    this.#dialog = null;
  }

  #buildDialog() {
    this.#dialog = document.createElement('dialog');
    this.#dialog.className = 'gallery-dialog';
    this.#dialog.innerHTML = `
      <button class="gallery-dialog__close" aria-label="Close">&times;</button>
      <button class="gallery-dialog__prev" aria-label="Previous">&#8249;</button>
      <img class="gallery-dialog__img" src="" alt="" />
      <button class="gallery-dialog__next" aria-label="Next">&#8250;</button>
      <p class="gallery-dialog__caption"></p>
      <p class="gallery-dialog__counter"></p>
    `;

    this.#dialogImg = this.#dialog.querySelector('.gallery-dialog__img');

    this.#dialog.querySelector('.gallery-dialog__close')
      .addEventListener('click', () => this.#close());
    this.#dialog.querySelector('.gallery-dialog__prev')
      .addEventListener('click', () => this.#navigate(-1));
    this.#dialog.querySelector('.gallery-dialog__next')
      .addEventListener('click', () => this.#navigate(1));

    // Clicking the backdrop (the dialog element itself, not its children) closes the dialog
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#close();
    });

    // Arrow key navigation (ESC is handled natively by showModal)
    this.#dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#navigate(-1);
      if (event.key === 'ArrowRight') this.#navigate(1);
    });

    // Append to body so it's never clipped by overflow/stacking contexts
    document.body.appendChild(this.#dialog);
  }

  /** @type {(event: MouseEvent) => void} */
  #handleItemClick = (event) => {
    const item = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('[data-lightbox-src]')
        : null
    );
    if (!item) return;
    event.preventDefault();
    this.#currentIndex = this.#items.indexOf(item);
    this.#open();
  };

  #open() {
    const item = this.#items[this.#currentIndex];
    if (!item || !this.#dialog || !this.#dialogImg) return;

    const hiResSrc = item.dataset.lightboxSrc ?? '';
    const thumbEl = item.querySelector('img');
    const thumbSrc = thumbEl?.currentSrc || thumbEl?.src || '';
    const img = this.#dialogImg;
    const gen = ++this.#loadGeneration;

    // Snap to thumbnail immediately (already cached — correct image with no delay)
    img.style.opacity = '1';
    img.src = thumbSrc;
    img.alt = item.dataset.lightboxAlt ?? '';

    // Upgrade to hi-res silently once loaded — same image, just sharper
    if (hiResSrc && hiResSrc !== thumbSrc) {
      const hiRes = new Image();
      hiRes.onload = () => {
        if (this.#loadGeneration !== gen) return;
        img.src = hiResSrc;
      };
      hiRes.src = hiResSrc;
    }

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__caption'))
      .textContent = item.querySelector('.gallery__caption')?.textContent ?? '';

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__counter'))
      .textContent = `${this.#currentIndex + 1} / ${this.#items.length}`;

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__prev'))
      .hidden = this.#currentIndex === 0;

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__next'))
      .hidden = this.#currentIndex === this.#items.length - 1;

    this.#dialog.showModal();
  }

  /** @param {-1 | 1} direction */
  #navigate(direction) {
    const next = this.#currentIndex + direction;
    if (next < 0 || next >= this.#items.length) return;
    this.#currentIndex = next;
    this.#open();
  }

  #close() {
    this.#dialog?.close();
  }
}

if (!customElements.get('gallery-lightbox')) {
  customElements.define('gallery-lightbox', GalleryLightbox);
}
